"use client";

import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const event = e as BeforeInstallPromptEvent;
      setDeferredPrompt(event);
      setShowPrompt(true);
    };
    window.addEventListener("beforeinstallprompt" as string, handler);
    return () =>
      window.removeEventListener("beforeinstallprompt" as string, handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      toast.success("UniUI installed successfully!");
    }
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-24 right-6 z-50 flex items-center gap-3 rounded-2xl border border-y/20 bg-background/95 px-4 py-3 shadow-lg backdrop-blur-md max-w-sm">
      <Download className="h-5 w-5 text-y" />
      <div className="flex-1">
        <p className="text-xs font-black uppercase tracking-widest text-foreground">
          Install UniUI
        </p>
        <p className="text-xs text-muted-foreground">
          Add to home screen for offline access
        </p>
      </div>
      <Button
        size="sm"
        onClick={handleInstall}
        className="h-7 rounded-lg bg-y text-black text-xs font-black uppercase"
      >
        Install
      </Button>
      <button
        onClick={() => setShowPrompt(false)}
        className="rounded-lg p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
